import React, { useState, useEffect } from 'react';
import { createMutualFund, getMutualFunds, getMutualFundPayouts, payMutualFundPayout, getUsers, calculateMutualFundLeaderPayouts, updateMutualFund, deleteMutualFund, getImageUrl, deleteMutualFundPayout } from '../../model/api';
import Table from './common/Table.jsx';
import { RefreshIcon, PlusCircleIcon, UploadIcon, DocumentIcon, TrashIcon } from './common/Icons.jsx';
import { toast } from 'react-toastify';
import LoadingSpinner from './common/LoadingSpinner.jsx';

export const MutualFundTab = ({ userRole, userId }) => {
    const [activeSubTab, setActiveSubTab] = useState('entries');
    const [entries, setEntries] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showCalculateModal, setShowCalculateModal] = useState(false);
    const [selectedPayout, setSelectedPayout] = useState(null);
    const [searchTerm, setSearchTerm] = useState(''); // For Add Modal user search
    const [entrySearchTerm, setEntrySearchTerm] = useState(''); // For Table filtering
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // Default to current month

    // Helper to generate year options (e.g., current year - 2 to current year + 1)
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 4 }, (_, i) => currentYear - 2 + i);
    const months = [
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];

    const handleMonthChange = (e) => {
        const [year, _] = selectedMonth.split('-');
        setSelectedMonth(`${year}-${e.target.value}`);
    };

    const handleYearChange = (e) => {
        const [_, month] = selectedMonth.split('-');
        setSelectedMonth(`${e.target.value}-${month}`);
    };



    const [submitting, setSubmitting] = useState(false); // Loading state for add entry form


    // Form State
    const [formData, setFormData] = useState({
        advisor_id: '',
        commission_amount: '',
        hierarchy_percentage: '10',
        customer_name: 'MF Customer',
        transaction_date: new Date().toISOString().slice(0, 10)
    });

    const [editFormData, setEditFormData] = useState({
        commission_amount: '',
        customer_name: '',
        hierarchy_percentage: ''
    });

    // Payment Form State
    const [paymentData, setPaymentData] = useState({
        transaction_id: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'Bank Transfer',
        payment_notes: ''
    });

    // Calculate Form State
    const [calculateData, setCalculateData] = useState({
        month: new Date().toISOString().slice(0, 7),
        percentage: ''
    });

    useEffect(() => {
        if (activeSubTab === 'entries') {
            fetchEntries();
        } else {
            fetchPayouts();
        }

        if (userRole === 'super_admin') {
            fetchUsers();
        }
    }, [activeSubTab, selectedMonth, showAddModal]);

    const fetchUsers = async () => {
        try {

            // Request all users with a high limit - don't filter by role in API, do it client-side
            const result = await getUsers({ limit: 10000 });


            // Handle different response structures
            let allUsers = [];
            if (Array.isArray(result)) {
                allUsers = result;
            } else if (result.data && Array.isArray(result.data.users)) {
                allUsers = result.data.users;
            } else if (result.users && Array.isArray(result.users)) {
                allUsers = result.users;
            } else if (result.data && Array.isArray(result.data)) {
                allUsers = result.data;
            }



            const filtered = allUsers.filter(u => u && u.role === 'advisor');


            setUsers(filtered);

            if (filtered.length === 0) {
                console.warn('No advisors or leaders found!');
                toast.warning('No advisors or leaders found in the system.');
            } else {

            }
        } catch (error) {
            console.error('Failed to fetch users:', error);
            toast.error('Failed to load users. Please try again.');
        }
    };

    const fetchEntries = async () => {
        setLoading(true);
        try {
            const params = selectedMonth ? { month: selectedMonth } : {};
            const result = await getMutualFunds(params);
            if (result.ok) {
                setEntries(result.data);
            }
        } catch (error) {
            toast.error('Failed to fetch entries');
        } finally {
            setLoading(false);
        }
    };

    const fetchPayouts = async () => {
        setLoading(true);
        try {
            const params = selectedMonth ? { month: selectedMonth } : {};
            const result = await getMutualFundPayouts(params);
            if (result.ok) {
                setPayouts(result.data);
            }
        } catch (error) {
            toast.error('Failed to fetch payouts');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateEntry = async (e) => {
        e.preventDefault();

        // Validate required fields
        if (!selectedFile) {
            toast.warning('Please upload a document');
            return;
        }

        // Set loading state to show spinner and disable form
        setSubmitting(true);

        try {
            // Prepare form data for API submission
            const formDataToSend = new FormData();
            formDataToSend.append('advisor_id', formData.advisor_id);
            formDataToSend.append('commission_amount', formData.commission_amount);
            formDataToSend.append('customer_name', formData.customer_name);
            formDataToSend.append('transaction_date', formData.transaction_date);
            formDataToSend.append('hierarchy_percentage', formData.hierarchy_percentage);
            // Enable document upload
            if (selectedFile) {
                formDataToSend.append('document', selectedFile);
            }

            // Submit the mutual fund entry
            const result = await createMutualFund(formDataToSend);
            if (result.ok) {
                toast.success('Mutual Fund entry created successfully!');
                // Reset form and close modal
                setShowAddModal(false);
                setFormData({
                    advisor_id: '',
                    commission_amount: '',
                    hierarchy_percentage: '10',
                    customer_name: 'MF Customer',
                    transaction_date: new Date().toISOString().slice(0, 10)
                });
                setSelectedFile(null);
                setSearchTerm('');
                // Refresh the entries list
                fetchEntries();
            } else {
                toast.error(result.message || 'Failed to create entry');
            }
        } catch (error) {
            toast.error('Error creating entry');
        } finally {
            // Reset loading state
            setSubmitting(false);
        }
    };

    const handleMarkPaid = async () => {
        if (!paymentData.transaction_id) {
            toast.warning('Transaction ID is required');
            return;
        }
        try {
            // Check if selectedPayout is an aggregated record
            if (selectedPayout.isAggregated && selectedPayout.ids && selectedPayout.ids.length > 0) {
                // Process all IDs concurrently
                let successCount = 0;
                let failCount = 0;

                // We'll use Promise.all to send requests in parallel
                const promises = selectedPayout.ids.map(id =>
                    payMutualFundPayout(id, paymentData)
                        .then(result => result.ok ? 1 : 0)
                        .catch(() => 0)
                );

                const results = await Promise.all(promises);
                successCount = results.reduce((a, b) => a + b, 0);
                failCount = results.length - successCount;

                if (successCount > 0) {
                    toast.success(`Marked ${successCount} payouts as paid!`);
                    if (failCount > 0) toast.warning(`${failCount} failed to update.`);
                    setShowPaymentModal(false);
                    setPaymentData({ transaction_id: '', payment_date: new Date().toISOString().split('T')[0], payment_method: 'Bank Transfer', payment_notes: '' });
                    fetchPayouts();
                } else {
                    toast.error('Failed to update payouts');
                }

            } else {
                // Single payout fallback
                const result = await payMutualFundPayout(selectedPayout._id, paymentData);
                if (result.ok) {
                    toast.success('Payout marked as paid');
                    setShowPaymentModal(false);
                    setPaymentData({ transaction_id: '', payment_date: new Date().toISOString().split('T')[0], payment_method: 'Bank Transfer', payment_notes: '' });
                    fetchPayouts();
                } else {
                    toast.error('Failed to update payout');
                }
            }
        } catch (error) {
            console.error('Error in bulk pay:', error);
            toast.error('Error updating payout');
        }
    };

    const handleCalculatePayouts = async () => {
        if (!calculateData.percentage) {
            toast.warning('Please enter hierarchy percentage');
            return;
        }

        if (!window.confirm(`Are you sure you want to calculate for ${calculateData.month}? \n\nWARNING: This will DELETE ALL existing payouts (including PAID ones) for this month and regenerate them.\nThis action cannot be undone.`)) {
            return;
        }
        try {
            const result = await calculateMutualFundLeaderPayouts(calculateData);
            if (result.ok) {
                toast.success(`Created ${result.payoutsCreated} leader payouts for ${calculateData.month}`);
                setShowCalculateModal(false);
                setCalculateData({ month: new Date().toISOString().slice(0, 7), percentage: '' });
                fetchPayouts();
            } else {
                toast.error(result.message || 'Failed to calculate payouts');
            }
        } catch (error) {
            toast.error('Error calculating payouts');
        }
    };

    const handleUpdateEntry = async (e) => {
        e.preventDefault();
        try {
            const result = await updateMutualFund(selectedEntry._id, editFormData);
            if (result.ok) {
                toast.success('Entry updated successfully!');
                setShowEditModal(false);
                setSelectedEntry(null);
                fetchEntries();
            } else {
                toast.error(result.message || 'Failed to update entry');
            }
        } catch (error) {
            toast.error('Error updating entry');
        }
    };

    const handleDeleteEntry = async (entryId) => {
        if (!window.confirm('Are you sure you want to delete this entry? This will also delete associated payouts.')) {
            return;
        }
        try {
            const result = await deleteMutualFund(entryId);
            if (result.ok) {
                toast.success('Entry deleted successfully!');
                fetchEntries();
            } else {
                toast.error(result.message || 'Failed to delete entry');
            }
        } catch (error) {
            toast.error('Error deleting entry');
        }
    };

    const filteredUsers = users.filter(u =>
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const entryColumns = [
        { header: 'Date', accessor: (item) => new Date(item.transaction_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) },

        { header: 'Advisor', accessor: (item) => item.advisor_id?.name || 'N/A' },
        { header: 'Commission', accessor: (item) => `₹${item.commission_amount}` },
        // Add payout status for advisors and leaders
        ...(userRole === 'advisor' || userRole === 'leader' || userRole === 'health_plus_partner' ? [{
            header: 'Payment Status', accessor: (item) => {
                const payout = item.user_payout;
                if (!payout) {
                    return <span className="px-2 py-1 rounded bg-gray-100 text-gray-800 text-xs">N/A</span>;
                }
                return (
                    <div className="flex flex-col gap-1">
                        <span className={`px-2 py-1 rounded text-xs ${payout.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {payout.status === 'paid' ? '✓ Paid' : 'Unpaid'}
                        </span>
                        {payout.status === 'paid' && payout.transaction_id && (
                            <span className="text-xs text-gray-600">Txn: {payout.transaction_id}</span>
                        )}
                        {payout.status === 'paid' && (payout.payment_date || payout.paid_at) && (
                            <span className="text-xs text-gray-500">{new Date(payout.payment_date || payout.paid_at).toLocaleDateString('en-IN')}</span>
                        )}
                    </div>
                );
            }
        }] : []),
        ...(userRole === 'super_admin' || userRole === 'advisor' || userRole === 'leader' ? [{
            header: 'Document',
            accessor: (item) => {
                const canViewDoc = userRole === 'super_admin' || (item.advisor_id && (item.advisor_id._id === userId || item.advisor_id === userId));

                if (item.document && canViewDoc) {
                    return (
                        <a
                            href={getImageUrl(item.document)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1"
                        >
                            <DocumentIcon className="w-4 h-4" />
                            View Doc
                        </a>
                    );
                }
                return <span className="text-gray-400">No doc</span>;
            }
        }] : []),
        ...(userRole === 'super_admin' ? [{
            header: 'Actions', accessor: (item) => (
                <div className="flex gap-2">

                    <button
                        onClick={() => handleDeleteEntry(item._id)}
                        className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                    >
                        Delete
                    </button>
                </div>
            )
        }] : [])
    ];

    const payoutColumns = [
        { header: 'Date', accessor: (item) => new Date(item.created_at).toLocaleDateString() },
        { header: 'User', accessor: (item) => item.user_id?.name || 'N/A' },
        { header: 'Role', accessor: (item) => item.role },
        { header: 'Amount', accessor: (item) => `₹${item.amount.toFixed(2)}` },
        {
            header: 'Status', accessor: (item) => (
                <span className={`px-2 py-1 rounded ${item.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {item.status}
                </span>
            )
        },
        {
            header: 'Action', accessor: (item) => (
                item.status === 'unpaid' && userRole === 'super_admin' ? (
                    <button
                        onClick={() => { setSelectedPayout(item); setShowPaymentModal(true); }}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                    >
                        Pay
                    </button>
                ) : item.status === 'paid' ? (
                    <div className="flex flex-col">
                        <span className="text-xs text-gray-500">Txn: {item.transaction_id}</span>
                        <span className="text-xs text-gray-400">{new Date(item.payment_date || item.paid_at).toLocaleDateString('en-IN')}</span>
                    </div>
                ) : null
            )
        }
    ];

    const handleDeletePayout = async (payout) => {
        if (!window.confirm('Are you sure you want to delete this payout? This action cannot be undone.')) {
            return;
        }

        try {
            if (payout.isAggregated && payout.ids && payout.ids.length > 0) {
                // Bulk delete
                const promises = payout.ids.map(id => deleteMutualFundPayout(id));
                await Promise.all(promises);
                toast.success(`Deleted ${payout.ids.length} payouts`);
            } else {
                // Single delete
                await deleteMutualFundPayout(payout._id);
                toast.success('Payout deleted successfully');
            }
            fetchPayouts();
        } catch (error) {
            console.error('Delete payout error:', error);
            toast.error('Failed to delete payout');
        }
    };

    return (
        <div className="space-y-6">
            {/* Sub-tabs */}
            <div className="flex border-b border-gray-200">
                <button
                    onClick={() => setActiveSubTab('entries')}
                    className={`py-2 px-4 font-medium text-sm focus:outline-none ${activeSubTab === 'entries' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    Monthly Trial Commission
                </button>
                {userRole === 'super_admin' && (
                    <button
                        onClick={() => setActiveSubTab('payouts')}
                        className={`py-2 px-4 font-medium text-sm focus:outline-none ${activeSubTab === 'payouts' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Payouts
                    </button>
                )}
            </div>

            {/* Content */}
            {activeSubTab === 'entries' && (
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-4">
                            <h3 className="text-lg font-semibold">Monthly Trial Commission</h3>
                            {/* Search Filter */}
                            {/* <input
                                type="text"
                                placeholder="Search Advisor..."
                                className="border rounded px-3 py-1 text-sm w-48"
                                value={entrySearchTerm}
                                onChange={e => setEntrySearchTerm(e.target.value)}
                            /> */}
                            <div>
                                <label className="text-sm text-gray-600 mr-2">Filter:</label>
                                <select
                                    className="border rounded px-2 py-1 text-sm mr-2"
                                    value={selectedMonth.split('-')[1]}
                                    onChange={handleMonthChange}
                                >
                                    {months.map(m => (
                                        <option key={m.value} value={m.value}>{m.label}</option>
                                    ))}
                                </select>
                                <select
                                    className="border rounded px-2 py-1 text-sm"
                                    value={selectedMonth.split('-')[0]}
                                    onChange={handleYearChange}
                                >
                                    {years.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {userRole === 'super_admin' && (
                            <button
                                onClick={() => setShowAddModal(true)}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
                            >
                                <PlusCircleIcon className="w-5 h-5 mr-2" />
                                Add Entry
                            </button>
                        )}
                    </div>
                    {loading ? <LoadingSpinner /> : (
                        <Table
                            columns={entryColumns}
                            data={entries.filter(item =>
                                !entrySearchTerm ||
                                item.advisor_id?.name?.toLowerCase().includes(entrySearchTerm.toLowerCase())
                            )}
                        />
                    )}
                </div>
            )}

            {activeSubTab === 'payouts' && (
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-4">
                            <h3 className="text-lg font-semibold">Mutual Fund Payouts</h3>
                            <div>
                                <label className="text-sm text-gray-600 mr-2">Filter:</label>
                                <select
                                    className="border rounded px-2 py-1 text-sm mr-2"
                                    value={selectedMonth.split('-')[1]}
                                    onChange={handleMonthChange}
                                >
                                    {months.map(m => (
                                        <option key={m.value} value={m.value}>{m.label}</option>
                                    ))}
                                </select>
                                <select
                                    className="border rounded px-2 py-1 text-sm"
                                    value={selectedMonth.split('-')[0]}
                                    onChange={handleYearChange}
                                >
                                    {years.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {userRole === 'super_admin' && (
                            <button
                                onClick={() => setShowCalculateModal(true)}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                            >
                                <RefreshIcon className="w-5 h-5 mr-2" />
                                <span className='hidden md:inline'>Calculate Leader Commission</span>
                                <span className='md:hidden'>Calc Comm</span>
                            </button>
                        )}
                    </div>

                    {/* All Payouts Table with Aggregation */}
                    <div>
                        {loading ? <LoadingSpinner /> : (
                            <Table
                                columns={[
                                    { header: 'Date', accessor: (item) => item.isAggregated ? `Month of ${new Date(item.created_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}` : new Date(item.created_at).toLocaleDateString() },
                                    { header: 'User', accessor: (item) => item.user_id?.name || 'N/A' },
                                    { header: 'Role', accessor: (item) => item.role },
                                    {
                                        header: 'Amount',
                                        accessor: (item) => (
                                            <div className="flex flex-col">
                                                <span className="font-medium">₹{item.amount.toFixed(2)}</span>
                                                {item.isAggregated && (
                                                    <span className="text-xs text-gray-500">({item.count} records)</span>
                                                )}
                                            </div>
                                        )
                                    },
                                    {
                                        header: 'Status', accessor: (item) => (
                                            <span className={`px-2 py-1 rounded ${item.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {item.status}
                                            </span>
                                        )
                                    },
                                    {
                                        header: 'Action', accessor: (item) => (
                                            <div className="flex gap-2">
                                                {item.status === 'unpaid' && userRole === 'super_admin' && (
                                                    <>
                                                        <button
                                                            onClick={() => { setSelectedPayout(item); setShowPaymentModal(true); }}
                                                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm whitespace-nowrap"
                                                        >
                                                            Pay {item.isAggregated ? 'All' : ''}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeletePayout(item)}
                                                            className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                                                            title="Delete Payout"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </>
                                                )}
                                                {item.status === 'paid' && (
                                                    <span className="text-xs text-gray-500">Txn: {item.transaction_id}</span>
                                                )}
                                            </div>
                                        )
                                    }
                                ]}
                                data={(() => {
                                    // Separate paid and unpaid
                                    const paidPayouts = payouts.filter(p => p.status === 'paid');
                                    const unpaidPayouts = payouts.filter(p => p.status === 'unpaid');

                                    // Aggregate unpaid payouts by user
                                    const aggregatedUnpaid = Object.values(unpaidPayouts.reduce((acc, curr) => {
                                        const userId = curr.user_id?._id || curr.user_id;
                                        if (!userId) return acc;

                                        if (!acc[userId]) {
                                            acc[userId] = {
                                                ...curr,
                                                amount: 0,
                                                ids: [],
                                                count: 0,
                                                isAggregated: true
                                            };
                                        }
                                        acc[userId].amount += curr.amount;
                                        acc[userId].ids.push(curr._id);
                                        acc[userId].count += 1;
                                        return acc;
                                    }, {}));

                                    // Aggregate paid payouts by user (same logic as unpaid)
                                    const aggregatedPaid = Object.values(paidPayouts.reduce((acc, curr) => {
                                        const userId = curr.user_id?._id || curr.user_id;
                                        if (!userId) return acc;

                                        if (!acc[userId]) {
                                            acc[userId] = {
                                                ...curr,
                                                amount: 0,
                                                ids: [],
                                                count: 0,
                                                isAggregated: true
                                            };
                                        }
                                        acc[userId].amount += curr.amount;
                                        acc[userId].ids.push(curr._id);
                                        acc[userId].count += 1;
                                        return acc;
                                    }, {}));

                                    // Combine aggregated unpaid and aggregated paid records
                                    // Sort by date descending
                                    return [...aggregatedUnpaid, ...aggregatedPaid].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                                })()}
                            />
                        )}
                    </div>
                </div>
            )}



            {/* Modals remain the same */}

            {/* Add Entry Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">Add Mutual Fund Entry</h3>
                        <form onSubmit={handleCreateEntry}>

                            {/* Month and Year Selection for New Entry */}
                            <div className="flex gap-4 mb-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium mb-1">Year</label>
                                    <select
                                        className="w-full border rounded px-3 py-2"
                                        value={new Date(formData.transaction_date).getFullYear()}
                                        onChange={e => {
                                            const d = new Date(formData.transaction_date);
                                            d.setFullYear(parseInt(e.target.value));
                                            setFormData({ ...formData, transaction_date: d.toISOString().slice(0, 10) });
                                        }}
                                    >
                                        {years.map(y => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex-1">
                                    <label className="block text-sm font-medium mb-1">Month</label>
                                    <select
                                        className="w-full border rounded px-3 py-2"
                                        value={String(new Date(formData.transaction_date).getMonth() + 1).padStart(2, '0')}
                                        onChange={e => {
                                            const d = new Date(formData.transaction_date);
                                            d.setMonth(parseInt(e.target.value) - 1);
                                            setFormData({ ...formData, transaction_date: d.toISOString().slice(0, 10) });
                                        }}
                                    >
                                        {months.map(m => (
                                            <option key={m.value} value={m.value}>{m.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Mutual Fund Advisor/Leader *</label>
                                <input
                                    type="text"
                                    className="w-full border rounded px-3 py-2 mb-2"
                                    placeholder="Search by name or email..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    disabled={submitting}
                                />
                                <div className="max-h-40 overflow-y-auto border rounded">
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.map(u => (
                                            <div
                                                key={u._id}
                                                onClick={() => {
                                                    if (!submitting) {
                                                        setFormData({ ...formData, advisor_id: u._id });
                                                        setSearchTerm(u.name);
                                                    }
                                                }}
                                                className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${formData.advisor_id === u._id ? 'bg-indigo-50' : ''} ${submitting ? 'cursor-not-allowed opacity-50' : ''}`}
                                            >
                                                <div className="font-medium">{u.name}</div>
                                                <div className="text-xs text-gray-500">{u.email} • {u.role}</div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-3 py-2 text-gray-500 text-sm">No users found</div>
                                    )}
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Commission Amount (₹) *</label>
                                <input
                                    type="number"
                                    className="w-full border rounded px-3 py-2"
                                    value={formData.commission_amount}
                                    onChange={e => setFormData({ ...formData, commission_amount: e.target.value })}
                                    required
                                    disabled={submitting}
                                />
                            </div>
                            {/* <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Hierarchy % *</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="w-full border rounded px-3 py-2"
                                    value={formData.hierarchy_percentage}
                                    onChange={e => setFormData({ ...formData, hierarchy_percentage: e.target.value })}
                                    required
                                    disabled={submitting}
                                />
                                <p className="text-xs text-gray-500 mt-1">Default is 10%. This controls the override rate for leaders.</p>
                            </div> */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Document (Image/PDF) *</label>
                                {/* Note: File upload is required for form validation but not uploaded to GCP */}
                                <input
                                    type="file"
                                    accept="image/*,application/pdf"
                                    className="w-full border rounded px-3 py-2"
                                    onChange={e => setSelectedFile(e.target.files[0])}
                                    required
                                    disabled={submitting}
                                />
                                {selectedFile && (
                                    <p className="text-xs text-gray-600 mt-1">Selected: {selectedFile.name}</p>
                                )}
                            </div>
                            <div className="flex justify-end gap-2">
                                <button type="button" onClick={() => { setShowAddModal(false); setSearchTerm(''); setSelectedFile(null); }} className="px-4 py-2 bg-gray-300 rounded" disabled={submitting}>Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded flex items-center gap-2" disabled={submitting}>
                                    {submitting ? (
                                        <>
                                            <LoadingSpinner size="sm" />
                                            Creating...
                                        </>
                                    ) : (
                                        'Submit'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            {showPaymentModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <h3 className="text-lg font-semibold mb-4">Mark Payout as Paid</h3>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Payment Date *</label>
                            <input
                                type="date"
                                className="w-full border rounded px-3 py-2"
                                value={paymentData.payment_date}
                                onChange={e => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Transaction ID *</label>
                            <input
                                type="text"
                                className="w-full border rounded px-3 py-2"
                                value={paymentData.transaction_id}
                                onChange={e => setPaymentData({ ...paymentData, transaction_id: e.target.value })}
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Payment Method</label>
                            <select
                                className="w-full border rounded px-3 py-2"
                                value={paymentData.payment_method}
                                onChange={e => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                            >
                                <option>Bank Transfer</option>
                                <option>UPI</option>
                                <option>Cash</option>
                            </select>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setShowPaymentModal(false)} className="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                            <button type="button" onClick={handleMarkPaid} className="px-4 py-2 bg-green-600 text-white rounded">Confirm</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Calculate Leader Payouts Modal */}
            {showCalculateModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <h3 className="text-lg font-semibold mb-4">Calculate Leader Commission</h3>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Month *</label>
                            <input
                                type="month"
                                className="w-full border rounded px-3 py-2"
                                value={calculateData.month}
                                onChange={e => setCalculateData({ ...calculateData, month: e.target.value })}
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Hierarchy % *</label>
                            <input
                                type="number"
                                step="0.01"
                                className="w-full border rounded px-3 py-2"
                                value={calculateData.percentage}
                                onChange={e => setCalculateData({ ...calculateData, percentage: e.target.value })}
                                placeholder="e.g., 10"
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">This percentage will be applied to all entries for the selected month</p>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setShowCalculateModal(false)} className="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                            <button type="button" onClick={handleCalculatePayouts} className="px-4 py-2 bg-green-600 text-white rounded">Calculate</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Entry Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <h3 className="text-lg font-semibold mb-4">Edit Mutual Fund Entry</h3>
                        <form onSubmit={handleUpdateEntry}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Commission Amount (₹) *</label>
                                <input
                                    type="number"
                                    className="w-full border rounded px-3 py-2"
                                    value={editFormData.commission_amount}
                                    onChange={e => setEditFormData({ ...editFormData, commission_amount: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Customer Name</label>
                                <input
                                    type="text"
                                    className="w-full border rounded px-3 py-2"
                                    value={editFormData.customer_name}
                                    onChange={e => setEditFormData({ ...editFormData, customer_name: e.target.value })}
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Hierarchy % *</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="w-full border rounded px-3 py-2"
                                    value={editFormData.hierarchy_percentage}
                                    onChange={e => setEditFormData({ ...editFormData, hierarchy_percentage: e.target.value })}
                                    required
                                />
                                <p className="text-xs text-gray-500 mt-1">Controls the override rate for leaders.</p>
                            </div>
                            <div className="flex justify-end gap-2">
                                <button type="button" onClick={() => { setShowEditModal(false); setSelectedEntry(null); }} className="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Update</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
